import { Button, type ButtonProps, keyframes, styled, useTheme } from '@mui/material'

import { FinalizationState } from './useFinalize'

const message = {
  [FinalizationState.NotStarted]: 'Create Quote',
  [FinalizationState.SavingConfiguration]: 'Saving configuration',
  [FinalizationState.GeneratingImages]: 'Generating images',
  [FinalizationState.GeneratingDesign]: 'Generating design',
  [FinalizationState.CreatingQuote]: 'Creating quote',
  [FinalizationState.Done]: 'Quote created', // Not used, just to make TS happy.
} as const

const progress = {
  [FinalizationState.NotStarted]: 0,
  [FinalizationState.SavingConfiguration]: 0,
  [FinalizationState.GeneratingImages]: 10,
  [FinalizationState.GeneratingDesign]: 40,
  [FinalizationState.CreatingQuote]: 70,
  [FinalizationState.Done]: 100,
} as const

const stripes = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 86px 0;
  }
`

const COLOR_FRONT = '#1f52a1'
const COLOR_BACK = '#5b87c6'

const StyledBackground = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  background: `repeating-linear-gradient(-45deg, ${COLOR_FRONT}, ${COLOR_FRONT} 15px, ${COLOR_BACK} 15px, ${COLOR_BACK} 30px)`,
  backgroundSize: '85px 100px',
  animation: `${stripes} 3s linear infinite`,
  transition: 'width 0.1s ease-in-out',
  zIndex: 0,
})

const StyledText = styled('span')({
  position: 'relative',
  zIndex: 1,
})

type FinalizationSubmitButtonProps = ButtonProps & {
  state: FinalizationState
}

export const FinalizationSubmitButton = ({ state, ...props }: FinalizationSubmitButtonProps) => {
  const theme = useTheme()

  return (
    <Button
      {...props}
      variant="contained"
      color="primary"
      disabled={state !== FinalizationState.NotStarted}
      sx={{
        width: '250px',
        position: 'relative',
        '&.Mui-disabled': {
          backgroundColor: '#b4b4b4',
        },
      }}
    >
      <StyledBackground style={{ width: `${progress[state]}%` }} />
      <StyledText style={{ color: theme.palette.primary.contrastText }}>{message[state]}</StyledText>
    </Button>
  )
}

type ProgressButtonProps = ButtonProps & {
  text: string
  progress: number
}

export const ProgressButton = ({ text, progress, ...props }: ProgressButtonProps) => {
  const theme = useTheme()

  return (
    <Button
      {...props}
      variant="contained"
      color="primary"
      sx={{
        width: '250px',
        position: 'relative',
        '&.Mui-disabled': {
          backgroundColor: '#b4b4b4',
        },
      }}
    >
      <StyledBackground style={{ width: `${progress}%` }} />
      <StyledText style={{ color: theme.palette.primary.contrastText }}>{text}</StyledText>
    </Button>
  )
}
